/**
 * Маппинг сохранённых/расшаренных результатов расчёта в формы компонентов.
 *
 * Общий код для DashboardView и EmbedView — ранее дублировался байт-в-байт.
 */
import { FISCAL_MONTH_KEYS } from './fiscalMonths';

/** Convert old sales_plan format (objects) → new SalesPlanData (arrays). Pass-through if already new. */
export function mapSalesPlan(raw: any): any {
  if (!raw) return undefined;
  // Already new format — tacos is an array
  if (Array.isArray(raw.tacos)) return raw;

  const result: any = {};

  // tacos: {m1: 20, m2: 20, ..., target: 20} → [{month:1, tacos:20}, ...]
  if (raw.tacos && typeof raw.tacos === 'object') {
    const entries: {month: number; tacos: number}[] = [];
    for (const [k, v] of Object.entries(raw.tacos)) {
      if (k === 'target') continue;
      const m = parseInt(k.replace('m', ''), 10);
      if (!isNaN(m)) entries.push({ month: m, tacos: Number(v) || 0 });
    }
    entries.sort((a, b) => a.month - b.month);
    result.tacos = entries;
    result.targetTacos = Number(raw.tacos.target) || 0;
  }

  // annualGrowth: {"2025": 0, ...} → salesGrowth: [{year, annualGrowth, monthlyGrowth}, ...]
  if (raw.annualGrowth && typeof raw.annualGrowth === 'object' && !Array.isArray(raw.annualGrowth)) {
    result.salesGrowth = Object.entries(raw.annualGrowth).map(([y, v]) => {
      const annual = Number(v) || 0;
      const mg = (Math.pow(1 + annual / 100, 1 / 12) - 1) * 100;
      return { year: Number(y), annualGrowth: annual, monthlyGrowth: +mg.toFixed(2) };
    }).sort((a, b) => a.year - b.year);
  }

  // monthlyPlan: {july: -37, ...} → monthlyGrowth2025: [{month:1, monthName:'july', growth:-37}, ...]
  // monthName в payload — канонический английский ключ ('july', 'august', ...),
  // чтобы XLSX-экспорт и шеринг не зависели от UI-локали.
  if (raw.monthlyPlan && typeof raw.monthlyPlan === 'object') {
    result.monthlyGrowth2025 = FISCAL_MONTH_KEYS.map((key, i) => ({
      month: i + 1,
      monthName: key,
      growth: Number((raw.monthlyPlan as any)[key]) || 0,
    }));
  }

  return result;
}

/**
 * `payload` из `ProductCalculationDetail` (`shared/api/client.ts`) хранится и
 * возвращается бэкендом КАК ЕСТЬ (`backend/services/product_calculations.py`) —
 * без обёртки `calculation_data`/`sales_plan`, которую `POST /api/save-result`
 * делает для standalone-истории (`backend/api/routes/results.py:64,69`). Значит
 * это ровно объект `buildBackendPayload()`: плоские snake_case-поля калькулятора
 * на верхнем уровне, `sales_plan_data` уже в НОВОМ формате (`SalesPlanData`,
 * не старый `{m1: ..., m2: ...}`).
 *
 * `mapCalcData`/`mapSalesPlan` ниже и `DashboardView.calcInitial` ждут форму
 * standalone-результатов (`calculation_data`, `sales_plan`). Эта функция
 * приводит форму product-calculations к ней — чтобы загрузка расчёта карточки
 * товара использовала ОДИН и тот же конвейер `setLoadedResult`, а не заводила
 * второй.
 */
export function productCalculationPayloadToLoadedResult(
  payload: Record<string, unknown>,
): Record<string, unknown> {
  return {
    calculation_data: payload,
    sales_plan: payload.sales_plan_data,
    budget_data: payload.budget_data,
    notes: payload.notes,
  };
}

export function mapCalcData(raw: Record<string, unknown> | undefined) {
  if (!raw) return undefined;
  return {
    purchasePrice: raw.purchase_price as number,
    shippingCostPerKg: raw.shipping_cost_per_kg as number,
    unitWeightKg: raw.unit_weight_kg as number,
    storageFee: (raw.storage_fee ?? raw.storage_fee_per_unit) as number,
    sellingPrice: raw.selling_price as number,
    referralFee: raw.referral_fee as number,
    fbaFee: raw.fba_fee as number,
    otherFees: raw.other_fees as number,
    ppcCost: raw.ppc_cost as number,
    conversionRate: raw.conversion_rate as number,
    ppcSalesPercentage: raw.ppc_sales_percentage as number,
    returnRate: raw.return_rate as number,
    resellableReturns: raw.resellable_returns as number,
    monthlySales: raw.monthly_sales as number,
    batchQuantity: raw.batch_quantity as number,
    couponPromoEnabled: raw.coupon_promo_enabled as boolean ?? true,
    dims:
      raw.length_in != null && raw.width_in != null && raw.height_in != null
        ? {
            l: raw.length_in as number,
            w: raw.width_in as number,
            h: raw.height_in as number,
          }
        : undefined,
    asin: (raw.parent_asin as string) || undefined,
    competitorAsins: Array.isArray(raw.competitors)
      ? (raw.competitors as { asin: string }[]).map((c) => c.asin)
      : undefined,
  };
}
